import { plain } from '../../lib/labels';
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { adminLinks } from '../../lib/adminLinks';
import { LoadMore, useCursorPages, type CursorPage } from '../../lib/useCursorPages';
import { QueryError } from '../../lib/QueryState';
import { useDebounced } from '../../lib/useDebounced';
import { dayParam, oneOf, useUrlState } from '../../lib/urlState';

interface AuditLogItem {
  id: string;
  actorId: string | null;
  actorType: 'admin' | 'user' | 'system';
  action: string;
  targetType: string;
  targetId: string | null;
  beforeDiff: Record<string, unknown> | null;
  afterDiff: Record<string, unknown> | null;
  ipAddress: string | null;
  traceId: string | null;
  createdAt: string;
}

/** The entity kinds an audit row can be about that have a page in the panel. */
const TARGET_TYPES = ['User', 'Listener', 'CallSession', 'PaymentOrder', 'PayoutRequest'] as const;
type TargetType = (typeof TARGET_TYPES)[number];

/** A link to the thing an audit row is about, when the panel has a page for it. */
function targetLink(type: string, id: string | null): string | null {
  if (!id) return null;
  switch (type) {
    case 'User':
      return adminLinks.user(id);
    case 'Listener':
      return adminLinks.listener(id);
    case 'CallSession':
    case 'Call':
      return adminLinks.call(id);
    case 'PaymentOrder':
      return adminLinks.recharge(id);
    default:
      return null;
  }
}

/**
 * Admin activity. Filters live in the URL (?action= ?actorId= ?targetType=
 * ?targetId= ?from= ?to=) and the list pages back through the whole log with
 * "Load more" — it used to stop at the newest 50 rows with no way further.
 */
export function AuditLogPage() {
  const { get, set } = useUrlState();
  const action = get('action') ?? '';
  const actorId = get('actorId') ?? '';
  const targetType = oneOf(get('targetType'), TARGET_TYPES);
  const targetId = get('targetId') ?? '';
  const from = dayParam(get('from'));
  const to = dayParam(get('to'));
  const [viewing, setViewing] = useState<AuditLogItem | null>(null);

  // Typed locally, committed to the URL once typing pauses (pasting a UUID is one request).
  const [actionInput, setActionInput] = useState(action);
  const [actorInput, setActorInput] = useState(actorId);
  const [targetInput, setTargetInput] = useState(targetId);
  // Back / forward changes the URL underneath the inputs: keep them in step.
  useEffect(() => setActionInput(action), [action]);
  useEffect(() => setActorInput(actorId), [actorId]);
  useEffect(() => setTargetInput(targetId), [targetId]);
  const dAction = useDebounced(actionInput.trim());
  const dActor = useDebounced(actorInput.trim());
  const dTarget = useDebounced(targetInput.trim());
  useEffect(() => {
    if (dAction !== action || dActor !== actorId || dTarget !== targetId) {
      set({ action: dAction, actorId: dActor, targetId: dTarget });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dAction, dActor, dTarget]);

  const params = {
    action: action || undefined,
    actorId: actorId || undefined,
    targetType,
    targetId: targetId || undefined,
    since: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    until: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  };
  const list = useCursorPages<AuditLogItem>(['admin', 'audit', params], async (cursor) =>
    (await api.get<CursorPage<AuditLogItem>>('/admin/audit-log', { params: { ...params, cursor, limit: 50 } })).data,
  );
  const isFiltered = Boolean(action || actorId || targetType || targetId || from || to);

  const columns: TableColumnsType<AuditLogItem> = [
    {
      title: 'When',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
      width: 180,
    },
    {
      title: 'Done by',
      render: (_: unknown, row) => (
        <Space size={6}>
          <Tag color={row.actorType === 'admin' ? 'magenta' : row.actorType === 'system' ? 'default' : 'blue'}>
            {row.actorType}
          </Tag>
          {row.actorId ? (
            <a title="Show only this actor's activity" onClick={() => setActorInput(row.actorId!)}>
              <span style={{ fontSize: 11 }}>{row.actorId.slice(0, 8)}</span>
            </a>
          ) : (
            <span style={{ fontSize: 11, color: '#8B8B8B' }}>—</span>
          )}
        </Space>
      ),
    },
    { title: 'Action', dataIndex: 'action', render: (a: string) => <span title={a}>{plain(a)}</span> },
    {
      title: 'About',
      render: (_: unknown, row) => {
        const to = targetLink(row.targetType, row.targetId);
        return (
          <Space size={6}>
            <Tag>{plain(row.targetType)}</Tag>
            {row.targetId ? (
              to ? (
                <Link to={to} style={{ fontSize: 11 }}>
                  {row.targetId.slice(0, 8)}
                </Link>
              ) : (
                <Typography.Text copyable={{ text: row.targetId }} style={{ fontSize: 11, color: '#8B8B8B' }}>
                  {row.targetId.slice(0, 8)}
                </Typography.Text>
              )
            ) : (
              <span style={{ fontSize: 11, color: '#8B8B8B' }}>—</span>
            )}
          </Space>
        );
      },
    },
    { title: 'Network address', dataIndex: 'ipAddress', responsive: ['lg'] },
    {
      title: '',
      render: (_: unknown, row) => <a onClick={() => setViewing(row)}>Details</a>,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Admin activity
      </Typography.Title>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            aria-label="Action"
            placeholder="action (e.g. listener.approve)"
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            style={{ minWidth: 240 }}
            allowClear
          />
          <Input
            aria-label="Actor admin id"
            placeholder="actor admin id"
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            style={{ minWidth: 240 }}
            allowClear
          />
          <Select<TargetType | undefined>
            allowClear
            aria-label="About"
            placeholder="About: anything"
            value={targetType}
            onChange={(v) => set({ targetType: v ?? null })}
            style={{ minWidth: 170 }}
            options={TARGET_TYPES.map((t) => ({ value: t, label: plain(t) }))}
          />
          <Input
            aria-label="Target id"
            placeholder="target id (user, listener, call…)"
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            style={{ minWidth: 260 }}
            allowClear
          />
          <DatePicker.RangePicker
            aria-label="Date range"
            key={`${from}|${to}`}
            onChange={(_d, [f, t]) => set({ from: f || null, to: t || null })}
          />
          {(from || to) && (
            <Tag closable color="blue" onClose={() => set({ from: null, to: null })}>
              {from === to ? from : `${from || '…'} → ${to || '…'}`}
            </Tag>
          )}
          {isFiltered && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setActionInput('');
                setActorInput('');
                setTargetInput('');
                set({ action: null, actorId: null, targetType: null, targetId: null, from: null, to: null });
              }}
            >
              Clear filters
            </Button>
          )}
        </Space>
        {list.isError ? (
          <QueryError error={list.error} what="admin activity" onRetry={list.refetch} />
        ) : (
          <>
            <Table
              rowKey="id"
              loading={list.isLoading}
              dataSource={list.rows}
              columns={columns}
              locale={{ emptyText: isFiltered ? 'No admin activity matches these filters.' : 'No admin activity yet.' }}
              pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100] }}
            />
            <LoadMore list={list} noun="entries" />
          </>
        )}
      </Card>

      <Drawer width={600} title={viewing ? plain(viewing.action) : ''} open={!!viewing} onClose={() => setViewing(null)}>
        {viewing && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={1} size="small" labelStyle={{ width: 110 }}>
              <Descriptions.Item label="When">{new Date(viewing.createdAt).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Action">
                <code style={{ fontSize: 12 }}>{viewing.action}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Actor">
                {viewing.actorType} ·{' '}
                {viewing.actorId ? <Typography.Text copyable>{viewing.actorId}</Typography.Text> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Target">
                {plain(viewing.targetType)} ·{' '}
                {viewing.targetId ? (
                  targetLink(viewing.targetType, viewing.targetId) ? (
                    <Link to={targetLink(viewing.targetType, viewing.targetId) as string}>{viewing.targetId}</Link>
                  ) : (
                    <Typography.Text copyable>{viewing.targetId}</Typography.Text>
                  )
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="IP">{viewing.ipAddress ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Trace">{viewing.traceId ?? '—'}</Descriptions.Item>
            </Descriptions>
            <Card size="small" title="Before">
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {viewing.beforeDiff ? JSON.stringify(viewing.beforeDiff, null, 2) : '—'}
              </pre>
            </Card>
            <Card size="small" title="After">
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {viewing.afterDiff ? JSON.stringify(viewing.afterDiff, null, 2) : '—'}
              </pre>
            </Card>
          </Space>
        )}
      </Drawer>
    </Space>
  );
}
